<template>
  <app-panel :class="$style.panel">
    <bulk-add-header
      :back-link="backLink"
      :title="title"
      :subtitle="subtitle"
      :focused-field="focusedField"
      @delete-all="onClickDeleteAll"
    />

    <div :class="$style.wrapper" v-if="status === ACCOUNTS_STATUSES.loaded">
      <div :class="$style.container">
        <div :class="$style.payments">
          <component
            :is="rowComponent || defaultRowComponent"
            v-for="(payment, index) in payments"
            :ref="`row[${index}]`"
            :key="payment.id"
            :template="rowTemplate"
            :mobile-layout="isMobileLayout"
            :value="payment.body"
            :errors="errors[payment.id] ? errors[payment.id].errors : {}"
            :index="index"
            :select-data="selectData"
            :aside-checkbox="rowAsideCheckbox"
            :custom-checkbox="rowAsideCheckbox"
            @input="onUpdatePayment($event, index)"
            @delete="onDeleteRow(index)"
            @add-row="onAddRow(index)"
            @focusField="onFocusField"
            @blurField="onBlurField"
          />
        </div>

        <div :class="$style.addButtonContainer">
          <vs-button
            :class="$style.addButton"
            size="large"
            icon="add"
            color="#fff"
            @click="onAddRow()"
          >
            Добавить строку
          </vs-button>
        </div>
      </div>
    </div>

    <div :class="$style.loader" v-else-if="status === ACCOUNTS_STATUSES.loading">
      <div class="block-loading mb-4" />
    </div>

    <div :class="$style.footer">
      <div :class="$style.block">
        <div v-if="showTotal" class="text-xl">Общая сумма: {{ totalSum }} руб.</div>
        <div :class="$style.footerAside">
          <div :class="$style.errors" v-if="errorsCount">Строк с ошибками: <b>{{ errorsCount }}</b></div>
          <vs-button type="border" size="large" @click="onSavePayments">
            {{ createButtonText || 'Сформировать платежи' }}
          </vs-button>
        </div>
      </div>
    </div>
  </app-panel>
</template>

<script>
import arrayToIdIndex  from "@/client/helpers/arrayToIdIndex";
import BulkAddHeader   from "./BulkAddHeader";
import BulkAddRow      from "./BulkAddRow";
import notifyHttpError from "@/mixins/notifyHttpError";

const ACCOUNTS_STATUSES = {
  mounted: 0,
  loading: 1,
  loaded:  2,
};

const SENDING_STATUSES = {
  canSave: 0,
  saving:  1,
  saved:   2,
};


export default {
  name:       "BulkAdd",
  props:      [
    'title',
    'subtitle',
    'deleteAllMessage',
    'successSaveMessage',
    'createButtonText',
    'backLink',
    'rowTemplate',
    'mobileBreakpoint',
    'emptyRow',
    'showCheckPrint',
    'getRows',
    'onCreate',
    'onUpdate',
    'onDelete',
    'onDeleteAll',
    'onSubmit',
    'parseRowBody',
    'formatRow',
    'rowComponent',
    'showTotal',
    'rowAsideCheckbox',
  ],
  components: { BulkAddHeader, BulkAddRow },
  mixins:     [notifyHttpError],
  data() {
    return {
      defaultRowComponent: BulkAddRow,
      ACCOUNTS_STATUSES:   ACCOUNTS_STATUSES,
      SENDING_STATUSES:    SENDING_STATUSES,
      sendingStatus:       SENDING_STATUSES.canSave,
      status:              ACCOUNTS_STATUSES.mounted,

      payments:       [],
      errors:         {},
      errorsCount:    0,
      selectData:     {
        apartments: [],
      },
      focusedField:   null,
      isMobileLayout: false,
    };
  },
  computed: {
    selectedHouseId() {
      return this.$store.getters['appUser/selectedHouse'].id;
    },
    totalSum() {
      return this.payments.reduce(( sum, payment ) => sum + (+payment.body.all_amount * 10000 || 0), 0) / 10000;
    },
  },
  methods:  {
    loadAccounts() {
      this.status = ACCOUNTS_STATUSES.loading;
      this.$http.get(`buildings/${this.selectedHouseId}/account_numbers`, { params: { buildings_id: this.selectedHouseId } }).then(
        ( { data } ) => {
          this.selectData.apartments = data.map(apartment => ({
            label:    apartment.account_number,
            value:    apartment.account_number,
            original: apartment,
          }));
          this.status = ACCOUNTS_STATUSES.loaded;
        },
        ( e ) => {
          this.notifyHttpError(e);
          this.selectData.apartments = [];
          this.status = ACCOUNTS_STATUSES.mounted;
        },
      );
    },

    loadRows() {
      return this.getRows(this.selectedHouseId).then(
        ( { data } ) => {
          if (!data.length) return this.initPayments();
          this.payments = data.map(this.parseRow);
        },
        this.notifyHttpError,
      );
    },

    createTempPayment( payment ) {
      return this.onCreate({
        buildings_id: this.selectedHouseId,
        ...this.formatRow(payment),
      }).catch(this.notifyHttpError);
    },

    // ======

    parseRow( row ) {
      return ({
        ...row,
        body: this.parseRowBody(JSON.parse(row.body)),
      });
    },

    getRowRef( index ) {
      const $ref = this.$refs[`row[${index}]`];
      return $ref ? $ref[0] : undefined;
    },

    focusRow( index ) {
      const $ref = this.getRowRef(index);
      if ($ref) $ref.focusFirst();
    },

    initPayments() {
      const payment = { ...this.emptyRow };
      this.createTempPayment(payment).then(( { data } ) => {
        this.payments = [this.parseRow(data)];
      });
      this.sendingStatus = SENDING_STATUSES.canSave;
    },

    onAddRow( index ) {
      if (index !== undefined && this.getRowRef(index + 1)) {
        this.focusRow(index + 1);
        return false;
      }

      const payment = { ...this.emptyRow };
      const lastPayment = this.payments[this.payments.length - 1];
      if (lastPayment) {
        payment.payment_create_date = lastPayment.body.payment_create_date;
        payment.payment_passage_date = lastPayment.body.payment_passage_date;
        payment.type = lastPayment.body.type;
      }
      this.createTempPayment(payment).then(( { data } ) => {
        this.payments.push(this.parseRow(data));
        if (index !== undefined) {
          this.$nextTick(() => {
            this.focusRow(this.payments.length - 1);
          });
        }
      });
    },

    onDeleteRow( index ) {
      this.onDelete(this.selectedHouseId, index)
        .then(() => {
          this.payments.splice(index, 1);
          if (!this.payments.length) this.initPayments();
        })
        .catch(this.notifyHttpError);
    },

    onUpdatePayment( payment, index ) {
      this.payments[index].body = payment;
      // this.$set(this.payments, index, payment);
      return this.onUpdate({
        buildings_id: this.selectedHouseId,
        index_id:     index,
        ...this.formatRow(payment),
      }).catch(this.notifyHttpError);
    },

    onClickDeleteAll() {
      this.$modal.openConfirm({ message: this.deleteAllMessage }).result
        .then(() => this.onDeleteAll(this.selectedHouseId))
        .then(this.initPayments)
        .catch(this.notifyHttpError);
    },

    onSavePayments() {
      this.$vs.loading();
      return this.onSubmit(this.selectedHouseId, this.payments).then(( result ) => {
        if (!result) return;
        this.$vs.notify({ color: 'success', title: 'Успешно', text: this.successSaveMessage });
        this.errors = {};
        this.errorsCount = 0;
        this.initPayments();
      }, ( e ) => {
        if (!e) return;
        const { response: { data } } = e;
        if (!Array.isArray(data)) this.notifyHttpError(e);
        this.errors = arrayToIdIndex(data);
        this.errorsCount = 0;
        for (let i in this.errors) this.errorsCount++;
      }).finally(() => {
        this.$vs.loading.close();
      });
    },

    onFocusField( fieldName ) {
      this.focusedField = fieldName;
    },

    onBlurField() {
      this.focusedField = null;
    },

    onWindowResize() {
      this.isMobileLayout = window.innerWidth < this.mobileBreakpoint;
    },
  },
  created() {
    this.loadAccounts();
    this.loadRows();
  },
  mounted() {
    if (this.mobileBreakpoint) {
      window.addEventListener('resize', this.onWindowResize);
      this.onWindowResize();
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  watch: {
    selectedHouseId() {
      this.loadAccounts();
      this.loadRows();
    },
    mobileBreakpoint() {
      this.onWindowResize();
    },
  },
};
</script>

<style lang="scss" module>
$max_width: 1300px;
$footer_height: 80px;
$button_height: 100px;
$mobile_breakpoint: 1720px;

.panel {
  background: #F6F6F6;
  flex: 1;
  position: relative;
}

.wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @include max-width($mobile_breakpoint) {
    overflow: hidden;
  }
}

.container {
  margin: 0 -15px;
  padding: 15px 0 $footer_height + $button_height;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.payments {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
}

.addButtonContainer {
  position: absolute;
  bottom: $footer_height;
  left: 0;
  width: 100%;
  padding: 0 15px;
}

.addButton {
  background: #fff;
  width: 100%;
  color: #989898;
  padding-top: 2rem !important;
  padding-bottom: 2rem !important;
}

.footer {
  position: fixed;
  bottom: 0;
  height: $footer_height;
  background: #fff;
  right: 15px;
  left: 274px;
  @include anim(.5s);
  z-index: 1000;

  :global(.content-area-reduced) & {
    left: 94px;
  }

  :global(.content-area-full) & {
    right: 2.2rem;
    left: 2.2rem;
  }

  .block {
    padding-left: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    @include max-width($mobile_breakpoint) {
      padding-right: 2rem;
    }
  }
}

.block {
  max-width: $max_width;
}

.loader {
  width: 100%;
  padding: 100px 0 0;
}

.saveButtonWrapper {
  position: relative;
}

.hint {
  opacity: .5;
}

.hintIcon {
  vertical-align: middle;
}

.topIcon {
  vertical-align: middle;
  transform: rotate(90deg);
  width: 12px;
  height: 12px;
}

.bottomIcon {
  vertical-align: middle;
  transform: rotate(-90deg);
}

.errors {
  color: color('danger');
  margin-right: 1rem;
}

.footerAside {
  margin-left: auto;
  display: flex;
  align-items: center;
}

</style>
